import React, { useState } from "react";
import {
  Box,
  Typography,
  Drawer,
  IconButton,
  Stack,
  Button,
} from "@mui/material";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import CommonTable1 from "app/components/Tables/CommonTable1";
import { selectActiveSession } from "app/modules/schools/selectors";
import { getBookTransaction } from "app/services/schoolReportingServices/library.service";
import ReturnBookDialog from "app/components/common/Dialogs/library/ReturnBookDialog";
import ExportButtonGroup from "app/components/common/ExportButtonGroup";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExportDialog from "app/components/common/exportDialog";
import PdfSummaryPreviewDialog from "app/components/common/Dialogs/PdfSummaryPreviewDialog";
import { Link } from "react-router-dom";
import appRoutes from "app/config/routes";

const VIEW_DRAWER_HEADER = [
  "Sr.No",
  "User Name",
  "Book",
  "Issue Date",
  "Due Date",
  "Return Date",
  "Fine Amount",
  "Status",
];

const ViewDrawer = ({
  open,
  onClose,
  viewData,
  getAllLibraryBooksRefetch,
  getLibraryBookSummaryRefetch,
}) => {
  const selectedSession = useSelector(selectActiveSession);

  // State for ReturnBookDialog
  const [returnDialog, setReturnDialog] = useState({
    open: false,
    transaction: null,
  });
  const [state, setState] = useState({
    checkExportOptions: false,
    checkExportOptionsPdf: false,
  });
  const [selectedOptions, setSelectedOptions] = useState(VIEW_DRAWER_HEADER);
  const [selectedOptionsForPdf, setSelectedOptionsForPdf] =
    useState(VIEW_DRAWER_HEADER);
  const [downloadPdf, setDownloadPdf] = useState(false);

  // Fetch book transactions for the selected book
  const {
    data: getBookTransactionData,
    isLoading: getBookTransactionLoading,
    isFetching: getBookTransactionFetching,
    refetch: refetchBookTransaction,
  } = useQuery({
    queryKey: ["getBookTransaction", viewData?._id, selectedSession],
    queryFn: () =>
      getBookTransaction({
        session: selectedSession,
        libraryBookDocId: viewData?._id,
      }),
    enabled: !!open && !!viewData?._id && !!selectedSession,
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    },
  });

  const handleReturnClick = (transaction) => {
    setReturnDialog({
      open: true,
      transaction: {
        ...transaction,
        bookTitle: viewData?.title || "-",
        finePerDay: viewData?.finePerDay || "-",
      },
    });
  };

  const handleCloseReturnDialog = () => {
    setReturnDialog({ open: false, transaction: null });
  };

  const handleReturnSuccess = () => {
    handleCloseReturnDialog();
    refetchBookTransaction();
    if (getAllLibraryBooksRefetch) getAllLibraryBooksRefetch();
    if (getLibraryBookSummaryRefetch) getLibraryBookSummaryRefetch();
  };
  // <Typography>
  //   {cell?.row?.original?.createdBy?.personName || "-"}
  // </Typography>
  const columns = [
    {
      header: "Sr. No.",
      Cell: ({ cell }) => <Typography>{parseInt(cell.row.id) + 1}</Typography>,
    },
    {
      header: "User Name",
      accessorKey: "userName",
      Cell: ({
        cell: {
          row: { original },
        },
      }) => {
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* <TableProfilePic image={} /> */}
            <Link
              to={`/${appRoutes.student_profile}/${original?.borrowerDetail?.personDocId}`}
            >
              <Typography>
                {original?.borrowerDetail?.personName || "-"}
              </Typography>
            </Link>
          </Stack>
        );
      },
    },
    {
      header: "Book",
      accessorKey: "book",
      Cell: () => <Typography>{viewData?.title || "-"}</Typography>,
    },
    {
      header: "Issue Date",
      accessorKey: "issuedDate",
      Cell: ({ cell }) => (
        <Typography>
          {cell?.row?.original?.issuedDate
            ? dayjs(cell?.row?.original?.issuedDate).format("DD MMM, YYYY")
            : "-"}
        </Typography>
      ),
    },
    {
      header: "Due Date",
      accessorKey: "dueDate",
      Cell: ({ cell }) => {
        return (
          <Typography>
            {cell?.row?.original?.dueDate
              ? dayjs(cell?.row?.original?.dueDate).format("DD MMM, YYYY")
              : "-"}
          </Typography>
        );
      }
    },
    {
      header: "Return Date",
      accessorKey: "returnedDate",
      Cell: ({ cell }) => {
        return (
          <Typography>
            {cell?.row?.original?.returnedDate
              ? dayjs(cell?.row?.original?.returnedDate).format("DD MMM, YYYY")
              : "-"}
          </Typography>
        );
      }
    },
    {
      header: "Overdue",
      accessorKey: "overdue",
      Cell: ({ cell }) => {
        const row = cell?.row?.original;
        // If returned, overdue is 0
        if (row?.status === "Returned") {
          return <Typography color="success.main">0 days left</Typography>;
        }
        const dueDate = row?.dueDate ? dayjs(row.dueDate) : null;
        const isOverdue =
          dueDate &&
          dayjs().isAfter(dueDate, "day") &&
          row?.status !== "Returned";
        if (!dueDate) {
          return <Typography>-</Typography>;
        }
        const daysDifference = dueDate.diff(dayjs(), "day");
        return (
          <Typography color={isOverdue ? "error" : "success.main"}>
            {isOverdue
              ? `${Math.abs(daysDifference)} days overdue`
              : `${Math.abs(daysDifference)} days left`}
          </Typography>
        );
      },
    },
    {
      header: "Fine",
      accessorKey: "fineAmount",
      Cell: ({ cell }) => (
        <Typography
          color={cell?.row?.original?.fineAmount > 0 ? "error" : "success.main"}
        >
          {cell?.row?.original?.fineAmount || 0}
        </Typography>
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
      Cell: ({ cell }) => (
        <Typography>{cell?.row?.original?.status || "-"}</Typography>
      ),
    },
    {
      header: "Actions",
      Cell: ({ cell }) => {
        const row = cell?.row?.original;
        // Only show Return button if not already returned
        if (row?.status === "Returned") return null;
        return (
          <Button
            variant="contained"
            color="secondary"
            size="small"
            sx={{
              borderRadius: "8px",
              textTransform: "capitalize",
              fontWeight: 500,
              fontSize: "13px",
              minWidth: "90px",
              boxShadow: "none",
            }}
            onClick={() => handleReturnClick(row)}
          >
            Return
          </Button>
        );
      },
    },
  ];

  // Prepare export data
  const exportArray = (getBookTransactionData?.data?.list || []).map(
    (item, idx) => ({
      "Sr.No": idx + 1,
      "User Name": item?.borrowerDetail?.personName || "",
      Book: viewData?.title || "",
      "Issue Date": item?.issuedDate
        ? dayjs(item?.issuedDate).format("DD MMM, YYYY")
        : "",
      "Due Date": item?.dueDate
        ? dayjs(item?.dueDate).format("DD MMM, YYYY")
        : "",
      "Return Date": item?.returnedDate
        ? dayjs(item?.returnedDate).format("DD MMM, YYYY")
        : "",
      "Fine Amount": item?.fineAmount ?? 0,
      Status: item?.status || "",
    })
  );

  // Excel Export Handler
  const handleExport = () => {
    const filteredExportArray = exportArray.map((item) => {
      const filteredItem = {};
      selectedOptions.forEach((option) => {
        filteredItem[option] = item[option];
      });
      return filteredItem;
    });
    const worksheet = XLSX.utils.json_to_sheet(filteredExportArray);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BookTransactions");
    XLSX.writeFile(workbook, "book_transactions.xlsx");
  };

  // PDF Export Handler
  const handleExportPdf = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const rows = exportArray.map((item) =>
      selectedOptionsForPdf.map((option) => item[option])
    );
    autoTable(doc, {
      head: [selectedOptionsForPdf],
      body: rows,
      startY: 20,
      headStyles: { fillColor: [39, 137, 253] },
      bodyStyles: { fontSize: 10 },
      theme: "grid",
    });
    doc.save("book_transactions.pdf");
    setDownloadPdf(false);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: "98%", md: "80vw" }, p: 2 } }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">Book Transactions</Typography>
          <Box></Box>
          {/* export */}
          <ExportButtonGroup
            showExcel
            showPdf={true}
            handleClickExcel={() =>
              setState((prev) => ({ ...prev, checkExportOptions: true }))
            }
            handleClickPdf={() => {
              setState((prev) => ({ ...prev, checkExportOptionsPdf: true }));
            }}
          />
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box sx={{ flex: 1 }}>
          <CommonTable1
            columns={columns}
            data={getBookTransactionData?.data?.list || []}
            loading={getBookTransactionLoading || getBookTransactionFetching}
            maxHeight="70vh"
          />
        </Box>
      </Drawer>
      <ReturnBookDialog
        open={returnDialog.open}
        onClose={handleCloseReturnDialog}
        refetch={handleReturnSuccess}
        transaction={returnDialog.transaction}
      />
      <ExportDialog
        open={state.checkExportOptions}
        handleClose={() =>
          setState((prev) => ({ ...prev, checkExportOptions: false }))
        }
        header={VIEW_DRAWER_HEADER}
        exportArray={exportArray}
        selectedOptions={selectedOptions}
        setSelectedOptions={setSelectedOptions}
        handleExport={handleExport}
        fileName="book_transactions"
      />
      <PdfSummaryPreviewDialog
        open={state.checkExportOptionsPdf}
        handleClose={() =>
          setState((prev) => ({ ...prev, checkExportOptionsPdf: false }))
        }
        data={VIEW_DRAWER_HEADER}
        setSelectedOptionsForPdf={setSelectedOptionsForPdf}
        selectedOptionsForPdf={selectedOptionsForPdf}
        handleExportPdf={() => setDownloadPdf(true)}
      />
      {downloadPdf && handleExportPdf()}
    </>
  );
};

export default ViewDrawer;
